import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, GitCommit, ShieldCheck, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const format = (value) => (value === null || value === undefined || value === "" ? "Not recorded" : String(value));
const Status = ({ value }) => <span className="font-medium">{format(value).replaceAll("_", " ")}</span>;

export const ReleaseHistoryAdminPageV53 = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyState, setHistoryState] = useState("loading");
  const [historyError, setHistoryError] = useState("");
  const [historyIntegrity, setHistoryIntegrity] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setHistoryState("loading");
    setHistoryError("");
    setHistoryEvents([]);
    setHistoryIntegrity("");
    // Independent reads: one failed endpoint must not hide the other result.
    const historyRequest = axios.get(`${API}/admin/release-history`)
      .then(({ data }) => {
        if (data?.available === true && Array.isArray(data.events) && data.integrity === "hash_chain_verified") {
          setHistoryEvents(data.events);
          setHistoryIntegrity(data.integrity);
          setHistoryState("available");
        } else if (data?.reason === "not_configured") {
          setHistoryState("not_configured");
        } else {
          setHistoryState("unavailable");
          setHistoryError("Release journal unavailable or integrity check failed. No historical events are displayed.");
        }
      })
      .catch((err) => {
        setHistoryState("unavailable");
        setHistoryError(err.response?.status === 403 ? "Graphics Admin access required for release history." : "Unable to load verified release history.");
      });
    try {
      const response = await axios.get(`${API}/admin/release-info`);
      setInfo(response.data);
    } catch (err) {
      setInfo(null);
      setError(err.response?.status === 403 ? "Graphics Admin access required." : "Unable to retrieve release information. Check the backend and try again.");
    } finally {
      await historyRequest;
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const manifest = info?.manifest?.available ? info.manifest : null;
  const certification = manifest?.certification || {};
  const deploymentEvents = historyEvents.filter((event) => String(event.event || "").toLowerCase().includes("deploy"));
  const verifiedEvents = historyEvents.filter((event) => String(event.event || "").toLowerCase().includes("verif") && String(event.outcome || "").toLowerCase() === "success");
  const rollbackEvents = historyEvents.filter((event) => String(event.event || "").toLowerCase().includes("rollback"));
  const rows = [
    ["Application version", manifest?.application_version],
    ["Packaged release", manifest?.release],
    ["Original build source commit", manifest?.source_commit_at_build],
    ["Post-deployment reconciliation commit", manifest?.reconciled_source_commit],
    ["Build timestamp (UTC)", manifest?.built_at_utc],
    ["Deployment timestamp (UTC)", manifest?.deployed_at_utc],
    ["Backend image recorded in manifest", manifest?.images?.backend],
    ["Frontend image recorded in manifest", manifest?.images?.frontend],
    ["Runtime release (unverified)", info?.runtime_release],
  ];
  return <main className="animate-fade-in space-y-6" data-testid="release-history-v53">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="text-3xl font-bold flex items-center gap-2"><History size={27} /> Release History</h1><p className="mt-2 text-muted-foreground">Read-only release evidence for Graphics Admin.</p></div>
      <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-secondary px-4 py-2.5 font-medium disabled:opacity-50"><RefreshCw size={16} /> Refresh</button>
    </div>
    <div role="status" className="rounded-xl border border-border bg-card p-4">
      <p className="font-semibold flex items-center gap-2"><ShieldCheck size={18} /> Historical record — not live deployment verification</p>
      <p className="mt-2 text-sm text-muted-foreground">The manifest describes a packaged historical release. Image tags and certification values are recorded claims, not independently verified live state. Runtime release verified: {info?.runtime_release_verified === true ? "Yes" : "No"}.</p>
    </div>
    {loading && <p role="status">Loading release information...</p>}
    {error && <p role="alert" className="rounded-xl border border-border p-4">{error}</p>}
    {!loading && !error && !manifest && <p role="status" className="rounded-xl border border-border p-4">Release manifest unavailable ({format(info?.manifest?.reason)}).</p>}
    {!loading && !error && manifest && <>
      <section className="rounded-xl border border-border bg-card p-5"><h2 className="mb-4 text-lg font-semibold flex items-center gap-2"><GitCommit size={18} /> Recorded release</h2>
        <dl className="grid gap-4 md:grid-cols-2">{rows.map(([key, value]) => <div key={key}><dt className="text-sm text-muted-foreground">{key}</dt><dd className="mt-1 break-all font-medium">{format(value)}</dd></div>)}</dl>
      </section>
      <section className="rounded-xl border border-border bg-card p-5"><h2 className="mb-4 text-lg font-semibold">Recorded certification</h2>
        <dl className="grid gap-4 md:grid-cols-2">{Object.entries(certification).map(([key, value]) => <div key={key}><dt className="text-sm text-muted-foreground">{key.replaceAll("_", " ")}</dt><dd><Status value={value} /></dd></div>)}</dl>
      </section>
    </>}
    <section className="rounded-xl border border-border bg-card p-5" data-testid="verified-release-journal">
      <h2 className="text-lg font-semibold">Deployment event journal</h2>
      <p className="mt-1 text-sm text-muted-foreground">Verified historical events only. Events do not prove which image is currently running; the hash chain detects changes but does not authenticate the writer.</p>
      {historyState === "loading" && <p role="status" className="mt-3">Loading release journal...</p>}
      {historyState === "not_configured" && <p role="status" className="mt-3">Release journal storage is not configured. No deployment events have been recorded here.</p>}
      {historyState === "unavailable" && <p role="alert" className="mt-3">{historyError}</p>}
      {historyState === "available" && <>
        <p className="mt-3 text-sm" role="status">Integrity: {historyIntegrity.replaceAll("_", " ")} · {historyEvents.length} event(s)</p>
        <p className="mt-2 text-xs text-muted-foreground" data-testid="release-event-summary">Recorded events: {deploymentEvents.length} deployment-related · {verifiedEvents.length} successful verification · {rollbackEvents.length} rollback-related. A deployment event is not proof of post-release certification.</p>
        {historyEvents.length === 0 ? <p className="mt-3 text-muted-foreground">No release events recorded.</p> :
          <div className="mt-4 space-y-3">{[...historyEvents].reverse().slice(0, 100).map((event, index) =>
            <details key={`${event.event_id || "event"}-${event.sequence ?? index}`} className="rounded-lg border border-border p-3">
              <summary className="cursor-pointer font-medium">{format(event.release)} — {format(event.event).replaceAll("_", " ")} — {format(event.occurred_at_utc)}</summary>
              <dl className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                {[["Event ID", event.event_id], ["Outcome", event.outcome], ["Source commit (recorded)", event.source_commit], ["Backend image (recorded)", event.backend_image], ["Frontend image (recorded)", event.frontend_image], ["Rollback reference", event.rollback_reference], ["Sequence", event.sequence], ["Record hash", event.record_hash], ["Notes", event.notes]].map(([label, value]) =>
                  <div key={label}><dt className="text-muted-foreground">{label}</dt><dd className="break-all">{format(value)}</dd></div>
                )}
              </dl>
              {event.verification && typeof event.verification === "object" && <div className="mt-3"><h3 className="font-medium">Recorded verification</h3><dl className="mt-2 grid gap-2 md:grid-cols-2">{Object.entries(event.verification).map(([key, value]) => <div key={key}><dt className="text-sm text-muted-foreground">{key.replaceAll("_", " ")}</dt><dd className="text-sm">{format(value)}</dd></div>)}</dl></div>}
            </details>
          )}{historyEvents.length > 100 && <p className="text-sm text-muted-foreground">Showing newest 100 events of {historyEvents.length}.</p>}</div>}
      </>}
    </section>
  </main>;
};
